// ABOUTME: Interstitial screen shown when a user selects an unbuilt data source connector.
// ABOUTME: Auto-fires interest registration on mount and provides back navigation.

import { useEffect, useRef } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { dataSourceApi } from '@/services/api/dataSourceClient';

interface ComingSoonScreenProps {
  sourceName: string;
  sourceType: string;
  logoSrc: string;
  onBack: () => void;
}

export function ComingSoonScreen({
  sourceName,
  sourceType,
  logoSrc,
  onBack,
}: ComingSoonScreenProps) {
  const hasFiredRef = useRef(false);

  // Auto-fire interest registration on mount (once per render lifecycle)
  useEffect(() => {
    if (hasFiredRef.current) return;
    hasFiredRef.current = true;

    dataSourceApi.registerInterest({ source_type: sourceType }).catch((err) => {
      // Interest registration is best-effort; don't block the user
      console.debug('Failed to register interest:', err);
    });
  }, [sourceType]);

  return (
    <div className="max-w-md mx-auto text-center px-6 py-16">
      {/* Logo */}
      <div className="flex justify-center mb-6">
        <img
          src={logoSrc}
          alt={sourceName}
          className="w-24 h-24 object-contain"
        />
      </div>

      {/* Source name */}
      <h2 className="text-2xl font-bold text-surface-900 mb-3">
        {sourceName}
      </h2>

      {/* Status message */}
      <p className="text-surface-600 mb-2">
        We're working on connecting {sourceName}
      </p>
      <p className="text-sm text-surface-500 mb-8">
        Your interest has been registered — we'll notify you when it's ready.
      </p>

      {/* Back button */}
      <div className="mb-6">
        <Button variant="primary" onClick={onBack} aria-label="Back to data sources">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to data sources
        </Button>
      </div>

      {/* Contact link */}
      <p className="text-sm text-surface-400">
        Need this urgently?{' '}
        <a
          href={`mailto:support@oleum.pro?subject=${encodeURIComponent(`Interest in ${sourceName} connector`)}`}
          className="text-primary-500 hover:text-primary-600 underline"
        >
          Contact us
        </a>
      </p>
    </div>
  );
}
